"use client";

import { ProductCard } from "@/modules/products/components/product-card";
import { ProductDetailsModal } from "@/modules/products/components/product-details-modal";
import {
  useCurrencyStore,
  useProductModalStore,
  useToastStore,
} from "@/store/index";
import { StoreProduct } from "@medusajs/types";
import { getPriceForCurrency, formatPriceWithCurrency, getSymbol } from "@/lib/util/pricing";

interface ProductsGridProps {
  products: StoreProduct[];
}

export function ProductsGrid({ products }: ProductsGridProps) {
  const { isModalOpen, selectedProduct, closeModal } = useProductModalStore();
  const { showToast } = useToastStore();
  const { currencyCode } = useCurrencyStore();

  // Helper to get price for the selected currency
  const getProductPrice = (product: StoreProduct): string => {
    const variant = product.variants?.[0];
    if (!variant) return formatPriceWithCurrency(0, currencyCode);
    
    const price = getPriceForCurrency(variant, currencyCode);
    return formatPriceWithCurrency(price, currencyCode);
  };

  // Helper to get original price (if different from current)
  const getProductOriginalPrice = (product: StoreProduct): string | undefined => {
    const variant = product.variants?.[0];
    if (!variant) return undefined;

    const currentPrice = getPriceForCurrency(variant, currencyCode);
    
    // Check if there's a calculated original price that's different
    const calculatedPrice = variant.calculated_price;
    if (calculatedPrice?.original_amount && 
        calculatedPrice.original_amount !== currentPrice) {
      return formatPriceWithCurrency(calculatedPrice.original_amount, currencyCode);
    }
    
    return undefined;
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            price={getProductPrice(product)}
            originalPrice={getProductOriginalPrice(product)}
          />
        ))}
        {products.length === 0 && (
          <p className="col-span-full text-gray-500">
            No se encontraron productos.
          </p>
        )}
      </div>

      {/* Product Details Modal */}
      {selectedProduct && (
        <ProductDetailsModal
          isOpen={isModalOpen}
          onClose={closeModal}
          product={selectedProduct}
          onAddToFavorites={() => {
            showToast(
              "Se ha añadido correctamente este producto a su lista de Favoritos."
            );
          }}
        />
      )}
    </>
  );
}
